/**
 * Maps a top-level route segment to the permission module that gates it.
 *
 * Used to decide what is worth preloading. It is deliberately keyed on the first
 * path segment only — the route table's own `path` strings — because that is all
 * a PreloadingStrategy is given about a lazy route.
 *
 * Only module keys that exist as a `*_MODULE_KEY` constant in the codebase are
 * listed. Anything absent (configurations, commission management, suitability
 * templates, documents library) is treated as ungated and preloads for everyone,
 * which is what every route did before. Extend this as those modules gain a
 * declared key — but only with a key you have verified, since guessing one just
 * suppresses preloading for that route across the board.
 *
 * Getting an entry wrong can only ever *reduce* what gets preloaded. It can
 * never block a navigation: the router still lazy-loads the chunk on demand.
 */
const ROUTE_SEGMENT_TO_MODULE_KEY: Readonly<Record<string, string>> = {
  'user-management': 'user_management',
  'branch-management': 'branch_management',
  'ar-management': 'ar_management',
  'cpd': 'cpd',
  'exam-zone': 'exam_zone',
  'cases': 'case_management',
  'clients': 'client_management',
  'quick-sourcing': 'quick_sourcing',
  'sourcing': 'quick_sourcing',
};

/** The permission module gating this route, or null when the route is ungated. */
export function moduleKeyForRoutePath(path: string | undefined): string | null {
  if (!path) {
    return null;
  }

  const segment = path.split('/').filter(Boolean)[0]?.toLowerCase();
  if (!segment) {
    return null;
  }

  return ROUTE_SEGMENT_TO_MODULE_KEY[segment] ?? null;
}
